/**
 * useAlwaysPlayingVideo — keeps a <video> element looping forever.
 *
 * iOS Safari / Capacitor WebView aggressively pauses muted-looped videos
 * after a few cycles, on scroll off-screen, on tab visibility change, and
 * on transient decode errors. This hook re-arms playback through ALL of
 * those failure modes.
 *
 * Returns a ref callback to attach to <video>. Caller MUST also set
 * autoPlay, loop, muted, playsInline on the element for first-paint
 * behaviour; this hook only guarantees re-arming after a pause.
 *
 * Defence stack (most aggressive on top):
 *  1. **800ms heartbeat** — always running while the element is mounted.
 *     Uses live `getBoundingClientRect()` so we don't depend on
 *     IntersectionObserver state. If element is on screen and paused,
 *     force play().
 *  2. **IntersectionObserver** — fires immediately when an element
 *     re-enters viewport (faster than the heartbeat).
 *  3. **visibilitychange** — when user returns to tab/app.
 *  4. **pause event listener** — iOS WebView pauses mid-loop; we
 *     immediately re-arm.
 *  5. **ended event** — `loop` attribute fails on iOS after 2 cycles;
 *     we manually rewind + play.
 *  6. **window scroll listener (passive, throttled)** — on every scroll
 *     event, if the video is visible and paused, kick it. This catches
 *     the case where the IO entry-leave-entry cycle is too slow for
 *     fast scroll-back-to-top.
 *
 * Updated 2026-05-15 (round 2) — more aggressive heartbeat + scroll
 * fallback after user reported the prior fix still leaving black pauses.
 */
import { useCallback, useEffect, useRef } from "react";

const isVisible = (el) => {
  if (!el || !el.getBoundingClientRect) return false;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
};

export const useAlwaysPlayingVideo = () => {
  const ref = useRef(null);

  const tryPlay = useCallback(() => {
    const v = ref.current;
    if (!v) return;
    v.playsInline = true;
    if (!v.paused) return;

    // CRITICAL iOS WebView recovery path:
    // When iOS evicts the video buffer (which it does AGGRESSIVELY for
    // memory pressure, especially after a navigation away and back, or
    // after the tile scrolls off-screen for >30s), the element ends up
    // with `readyState === 0` (HAVE_NOTHING). At this point .play() will
    // resolve immediately as a no-op and the tile stays black. The ONLY
    // recovery is to call .load() to re-fetch and re-decode the source,
    // then .play() again. We detect this by readyState < 2 (HAVE_CURRENT_DATA).
    if (v.readyState < 2 && v.currentSrc) {
      try { v.load(); } catch { /* ignore */ }
    }

    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        // iOS autoplay-without-gesture rejection — mute & retry once.
        if (err && (err.name === "NotAllowedError" || err.name === "AbortError")) {
          try {
            if (!v.muted) v.muted = true;
            const p2 = v.play();
            if (p2 && typeof p2.catch === "function") p2.catch(() => {});
          } catch { /* ignore */ }
        }
      });
    }
  }, []);

  const tryPlayIfVisible = useCallback(() => {
    const v = ref.current;
    if (!v || !isVisible(v)) return;
    tryPlay();
  }, [tryPlay]);

  const attach = useCallback((el) => {
    const prev = ref.current;
    if (prev && prev.__alwaysPlayCleanup) prev.__alwaysPlayCleanup();
    ref.current = el;
    if (!el) return;

    // IntersectionObserver — fires fast on viewport entry
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) tryPlay();
      }
    }, { threshold: 0.01, rootMargin: "150px" });
    io.observe(el);

    // visibilitychange
    const onVis = () => { if (!document.hidden) tryPlayIfVisible(); };
    document.addEventListener("visibilitychange", onVis);

    // pageshow — fires when user navigates back via browser history
    // (Safari bfcache or React Router). Catches the "drops → marketplace
    // → back to drops, video is blank" scenario the user reported.
    const onPageShow = () => { tryPlayIfVisible(); };
    window.addEventListener("pageshow", onPageShow);

    // popstate — fires on SPA route changes via React Router. Same intent
    // as pageshow but for client-side navigations.
    const onPopState = () => { setTimeout(tryPlayIfVisible, 100); };
    window.addEventListener("popstate", onPopState);

    // focus — when the tab regains focus we always re-arm
    const onFocus = () => tryPlayIfVisible();
    window.addEventListener("focus", onFocus);

    // pause / ended — always re-arm if still visible
    const onPause = () => { if (isVisible(el)) tryPlay(); };
    const onEnded = () => {
      try { el.currentTarget && (el.currentTarget.currentTime = 0); } catch {}
      try { el.currentTime = 0; } catch {}
      tryPlay();
    };
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);

    // Heartbeat — 800ms is responsive without burning battery
    const heartbeat = setInterval(tryPlayIfVisible, 800);

    // Window scroll — throttled to once per ~120ms
    let scrollThrottle = 0;
    const onScroll = () => {
      const now = Date.now();
      if (now - scrollThrottle < 120) return;
      scrollThrottle = now;
      tryPlayIfVisible();
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });

    el.__alwaysPlayCleanup = () => {
      clearInterval(heartbeat);
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("focus", onFocus);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [tryPlay, tryPlayIfVisible]);

  useEffect(() => {
    return () => {
      const el = ref.current;
      if (el && el.__alwaysPlayCleanup) el.__alwaysPlayCleanup();
    };
  }, []);

  return attach;
};

export default useAlwaysPlayingVideo;
